package top.wcpe.allincore.dsl.compiler.pipeline

import top.wcpe.allincore.dsl.compiler.diag.Diagnostic
import top.wcpe.allincore.dsl.compiler.diag.DiagnosticSeverity
import top.wcpe.allincore.dsl.compiler.source.LogicalSourcePath
import top.wcpe.allincore.dsl.compiler.source.SourceFile
import top.wcpe.allincore.dsl.compiler.source.SourcePosition

/** 编译前一次性完成路径规范化、排序与重复检查。 */
internal object CompilationSourceSet {
    data class Prepared(
        val components: List<SourceFile>,
        val themes: List<SourceFile>,
        val entry: SourceFile,
    ) {
        val sourceTexts: Map<String, String> = buildMap {
            (components + themes + entry).forEach { put(it.path, it.text) }
        }
    }

    sealed interface Result {
        data class Success(val sources: Prepared) : Result
        data class Failure(val diagnostics: List<Diagnostic>) : Result
    }

    fun prepare(
        components: List<SourceFile>,
        themes: List<SourceFile>,
        entry: SourceFile,
    ): Result {
        val normalized = mutableListOf<Pair<String, SourceFile>>()
        val invalidPaths = mutableListOf<String>()
        for (source in components + themes + entry) {
            val path = runCatching { LogicalSourcePath.normalize(source.path) }.getOrNull()
            if (path == null) {
                invalidPaths += escapePath(source.path)
            } else {
                normalized += path to SourceFile(path, source.text)
            }
        }
        if (invalidPaths.isNotEmpty()) {
            return Result.Failure(
                invalidPaths.distinct().sorted().map { path ->
                    noPositionDiagnostic("DSL_INVALID_SOURCE_PATH", "无效逻辑路径：$path")
                },
            )
        }

        val duplicatePaths = normalized.groupBy { it.first }
            .filterValues { it.size > 1 }
            .keys
            .sorted()
        if (duplicatePaths.isNotEmpty()) {
            return Result.Failure(
                duplicatePaths.map { path ->
                    noPositionDiagnostic("DSL_DUPLICATE_SOURCE_PATH", "规范化逻辑路径重复：$path")
                },
            )
        }

        val componentCount = components.size
        val themeCount = themes.size
        val normalizedSources = normalized.map { it.second }
        return Result.Success(
            Prepared(
                components = normalizedSources.take(componentCount).sortedBy { it.path },
                themes = normalizedSources.drop(componentCount).take(themeCount).sortedBy { it.path },
                entry = normalizedSources.last(),
            ),
        )
    }

    private fun escapePath(path: String): String = buildString(path.length) {
        for (char in path) {
            when (char) {
                '\\' -> append("\\\\")
                '\n' -> append("\\n")
                '\r' -> append("\\r")
                '\t' -> append("\\t")
                '\u0000' -> append("\\0")
                else -> append(char)
            }
        }
    }

    private fun noPositionDiagnostic(code: String, message: String): Diagnostic = Diagnostic(
        code = code,
        severity = DiagnosticSeverity.ERROR,
        message = message,
        position = SourcePosition.UNKNOWN,
    )
}
